import { SurfaceContext, VRContext } from '@features/analysis/rendering/Panels/Renderers/vtkManagers';
import { getManualVolumeFromSeriesId } from '@features/analysis/rendering/Panels/Renderers/CornerstoneManager';
import vtkRenderer from '@kitware/vtk.js/Rendering/Core/Renderer';
import vtkCoordinate from '@kitware/vtk.js/Rendering/Core/Coordinate';
import vtkActor from '@kitware/vtk.js/Rendering/Core/Actor';

export type Pt = { x: number; y: number };

export type ZoomSnapshot = {
  center: Pt;
  size: Pt;
  fill: Pt[];
};

export function getWorldUnitsPerPixel(renderer: any) {
  if (!renderer) return { x: 1, y: 1, cssToDevice: 1 } as const;

  const rw = renderer.getRenderWindow?.();
  const view = rw?.getViews?.()[0];
  if (!view) return { x: 1, y: 1, cssToDevice: 1 } as const;

  const [devW, devH] = view.getSize();
  const dpr = window.devicePixelRatio || 1;
  const cssW = devW / dpr;
  const cssH = devH / dpr;

  const camera = renderer.getActiveCamera?.();
  const isParallel = camera?.getParallelProjection?.();

  let wuppY: number;
  let wuppX: number;
  if (isParallel) {
    const ps = camera.getParallelScale();
    wuppY = (2 * ps) / (cssH || 1);
    wuppX = (2 * ps) / (cssW || 1);
  } else {
    const dist = camera.getDistance();
    const fovRad = (camera.getViewAngle() * Math.PI) / 180;
    const worldHeight = 2 * dist * Math.tan(fovRad / 2);
    const worldWidth = 2 * dist * Math.tan(fovRad / 2);
    wuppY = worldHeight / (cssH || 1);
    wuppX = worldWidth / (cssH || 1);
  }

  const aspect = (cssW || 1) / (cssH || 1);
  if (cssW >= cssH) wuppX = wuppY * aspect;
  else wuppY = wuppX * aspect;

  return { x: wuppX, y: wuppY, cssToDevice: dpr } as const;
}

export const dist2 = (a: Pt, b: Pt) => {
  const dx = a.x - b.x,
    dy = a.y - b.y;
  return dx * dx + dy * dy;
};

export function dedupeSequential(points: Pt[], eps = 2): Pt[] {
  if (points.length === 0) return [];
  const out: Pt[] = [points[0]];
  const eps2 = eps * eps;
  for (let i = 1; i < points.length; i++) {
    if (dist2(points[i], out[out.length - 1]) > eps2) out.push(points[i]);
  }
  return out;
}

export function snapClose(points: Pt[], eps = 6): Pt[] {
  if (points.length < 2) return points;
  const first = points[0],
    last = points[points.length - 1];
  if (dist2(first, last) <= eps * eps) {
    const p = [...points];
    p[p.length - 1] = { ...first };
    return p;
  }
  return points;
}

export function convexHull(points: Pt[]): Pt[] {
  if (points.length <= 3) return points.slice();
  const pts = points.slice().sort((a, b) => a.x - b.x || a.y - b.y);
  const cross = (o: Pt, a: Pt, b: Pt) =>
    (a.x - o.x) * (b.y - o.y) - (a.y - o.y) * (b.x - o.x);
  const lower: Pt[] = [];
  for (const p of pts) {
    while (
      lower.length >= 2 &&
      cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
    )
      lower.pop();
    lower.push(p);
  }
  const upper: Pt[] = [];
  for (let i = pts.length - 1; i >= 0; i--) {
    const p = pts[i];
    while (
      upper.length >= 2 &&
      cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
    )
      upper.pop();
    upper.push(p);
  }
  upper.pop();
  lower.pop();
  return lower.concat(upper);
}

export function finalizePolygon(
  points: Pt[],
  opts?: { dedup?: number; snap?: number; useHull?: boolean },
): Pt[] {
  const dedup = opts?.dedup ?? 1;
  const snap = opts?.snap ?? 4;
  const useHull = opts?.useHull ?? false;
  let out = dedupeSequential(points, dedup);
  out = snapClose(out, snap);
  if (useHull) out = convexHull(out);
  return out;
}

export function polylinePath(points: Pt[], closed = true): string {
  if (points.length === 0) return '';
  if (points.length === 1)
    return `M ${points[0].x},${points[0].y}${closed ? ' Z' : ''}`;
  const segs = points.map((p) => `${p.x},${p.y}`).join(' L ');
  return `M ${segs}${closed ? ' Z' : ''}`;
}

export function resizeSnapshotPath(snapshot: ZoomSnapshot, currentSize: Pt) {
  const scale = currentSize.y / (snapshot.size.y || 1);
  const currentCenter = {
    x: currentSize.x / 2,
    y: currentSize.y / 2,
  };

  const resizedPath = snapshot.fill.map((p) => ({
    x: currentCenter.x + (p.x - snapshot.center.x) * scale,
    y: currentCenter.y + (p.y - snapshot.center.y) * scale,
  }));

  const resizedCenter = {
    x: currentCenter.x,
    y: currentCenter.y,
  };

  return { path: resizedPath, center: resizedCenter };
}

export function getVtkDisplaySize(
  context: SurfaceContext | VRContext,
  renderer: any,
  fallback: Pt,
) {
  const canvas = (context as any).canvas as HTMLCanvasElement | undefined;
  if (canvas?.width && canvas?.height) {
    return { x: canvas.width, y: canvas.height };
  }

  const view = renderer?.getRenderWindow?.()?.getViews?.()?.[0];
  const size = view?.getSize?.();
  if (size?.[0] && size?.[1]) {
    return { x: size[0], y: size[1] };
  }

  const dpr = window.devicePixelRatio || 1;
  return {
    x: fallback.x * dpr,
    y: fallback.y * dpr,
  };
}

export function polygonToDisplayPoints(
  canvas: HTMLCanvasElement,
  poly: Pt[],
  width: number,
  height: number,
  renderer: vtkRenderer,
  selectedSeriesId: string | undefined,
  opts?: {
    flipY?: boolean;
    scaleX?: number;
    scaleY?: number;
    clipBounds?: {
      minX: number;
      minY: number;
      maxX: number;
      maxY: number;
    };
  },
): Float32Array {
  const DEBUG_RASTER = false;

  if (DEBUG_RASTER) console.time('polygonToDisplayPoints');

  if (
    !poly ||
    poly.length < 3 ||
    width <= 0 ||
    height <= 0 ||
    !selectedSeriesId
  ) {
    if (DEBUG_RASTER) console.timeEnd('polygonToDisplayPoints');
    return new Float32Array(0);
  }

  const volume = getManualVolumeFromSeriesId(selectedSeriesId);
  const spacing = volume?.imageData?.getSpacing();

  if (!volume || !spacing) {
    if (DEBUG_RASTER) console.timeEnd('polygonToDisplayPoints');
    return new Float32Array(0);
  }

  const scaleX = opts?.scaleX ?? 1;
  const scaleY = opts?.scaleY ?? 1;
  const shouldFlipY = opts?.flipY === true;

  const { y: wuppY } = getWorldUnitsPerPixel(renderer);
  const voxelWorldSize = spacing[1] || 1;
  const pixelsPerVoxel = voxelWorldSize / (wuppY || 1e-6);

  // 1 voxel당 최소 1 screen sample 보장.
  // 너무 작으면 ray 수가 폭증하므로 0.25px로 clamp.
  const sampleStepPx = Math.max(0.25, pixelsPerVoxel);
  const halfStep = sampleStepPx * 0.5;

  const pixelW = Math.max(1, Math.round(width));
  const pixelH = Math.max(1, Math.round(height));

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (let i = 0; i < poly.length; i++) {
    const p = poly[i];
    const x = p.x * scaleX;
    const y = p.y * scaleY;

    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }

  minX = Math.max(0, Math.floor(minX));
  minY = Math.max(0, Math.floor(minY));
  maxX = Math.min(pixelW - 1, Math.ceil(maxX));
  maxY = Math.min(pixelH - 1, Math.ceil(maxY));

  const clipBounds = opts?.clipBounds;
  if (clipBounds) {
    minX = Math.max(minX, Math.floor(clipBounds.minX));
    minY = Math.max(minY, Math.floor(clipBounds.minY));
    maxX = Math.min(maxX, Math.ceil(clipBounds.maxX));
    maxY = Math.min(maxY, Math.ceil(clipBounds.maxY));
  }
  if (minX > maxX || minY > maxY) {
    if (DEBUG_RASTER) console.timeEnd('polygonToDisplayPoints');
    return new Float32Array(0);
  }

  const bboxW = maxX - minX + 1;
  const bboxH = maxY - minY + 1;

  if (canvas.width !== bboxW) canvas.width = bboxW;
  if (canvas.height !== bboxH) canvas.height = bboxH;

  const ctx = canvas.getContext('2d', {
    willReadFrequently: true,
  })!;

  ctx.clearRect(0, 0, bboxW, bboxH);
  ctx.imageSmoothingEnabled = false;

  ctx.beginPath();
  ctx.moveTo(poly[0].x * scaleX - minX, poly[0].y * scaleY - minY);

  for (let i = 1; i < poly.length; i++) {
    ctx.lineTo(poly[i].x * scaleX - minX, poly[i].y * scaleY - minY);
  }

  ctx.closePath();
  ctx.fillStyle = '#000';
  ctx.fill();

  if (DEBUG_RASTER) console.time('getImageData');
  const img = ctx.getImageData(0, 0, bboxW, bboxH);
  if (DEBUG_RASTER) console.timeEnd('getImageData');

  if (DEBUG_RASTER) console.time('scanPixels');

  const maxSamplesX = Math.ceil(bboxW / sampleStepPx);
  const maxSamplesY = Math.ceil(bboxH / sampleStepPx);
  const fill = new Float32Array(maxSamplesX * maxSamplesY * 2);

  let fillPtr = 0;
  const data = img.data;

  for (let y = 0; y < bboxH; y += sampleStepPx) {
    const yy = y | 0;
    const rowOffset = yy * bboxW * 4;
    const py = minY + y + halfStep;
    const outY = shouldFlipY ? height - py : py;

    for (let x = 0; x < bboxW; x += sampleStepPx) {
      const xx = x | 0;
      const alphaIndex = rowOffset + xx * 4 + 3;

      if (data[alphaIndex] > 0) {
        fill[fillPtr++] = minX + x + halfStep;
        fill[fillPtr++] = outY;
      }
    }
  }

  if (DEBUG_RASTER) {
    console.timeEnd('scanPixels');
    console.log('fill points', fillPtr / 2);
    console.timeEnd('polygonToDisplayPoints');
  }

  return fill.subarray(0, fillPtr);
}


export function getVisibleActorsDisplayBounds(
  context: SurfaceContext | VRContext,
  renderer: any,
  pad = 100
) {
  const actors = renderer?.getActors?.() ?? [];
  const actorIds = context.actorIds;
  if (!actors.length || !actorIds) return undefined;

  const excludedActors = new Set<vtkActor>();
  ['Sagittal', 'Coronal', 'Axial'].forEach((key) => {
    const actor = actorIds[key] as vtkActor;
    if (actor) {
      excludedActors.add(actor);
    }
  });

  const displayCoord = vtkCoordinate.newInstance();
  displayCoord.setCoordinateSystemToWorld();

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  for (const actor of actors) {
    if (actor?.getVisibility?.() === false) continue;

    if (excludedActors.has(actor)) {
      continue;
    }

    const mapper = actor?.getMapper?.();
    const input = mapper?.getInputData?.();

    if (input?.getNumberOfPoints?.() === 0) continue;

    const b = actor?.getBounds?.();
    if (!b || b.length < 6 || b.some((v: number) => !Number.isFinite(v))) {
      continue;
    }

    const corners = [
      [b[0], b[2], b[4]],
      [b[1], b[2], b[4]],
      [b[0], b[3], b[4]],
      [b[0], b[2], b[5]],
      [b[1], b[3], b[4]],
      [b[1], b[2], b[5]],
      [b[0], b[3], b[5]],
      [b[1], b[3], b[5]],
    ];

    for (const p of corners) {
      displayCoord.setValue(p);
      const [x, y] = displayCoord.getComputedDisplayValue(renderer);

      minX = Math.min(minX, x);
      minY = Math.min(minY, y);
      maxX = Math.max(maxX, x);
      maxY = Math.max(maxY, y);
    }
  }

  if (![minX, minY, maxX, maxY].every(Number.isFinite)) {
    return undefined;
  }

  return {
    minX: Math.floor(minX - pad),
    minY: Math.floor(minY - pad),
    maxX: Math.ceil(maxX + pad),
    maxY: Math.ceil(maxY + pad),
  };
}
